// storage/CapacitorCache.ts
import {
  ConfigureOptions,
  MigrateResult,
  Preferences,
  PreferencesPlugin,
} from '@capacitor/preferences';
import { isCapacitorNative } from './Constants';
import { AsyncStorage } from 'oidc-client-ts';
import { reportError } from 'Utils/reportError';

class AsyncLocalStorage implements PreferencesPlugin {
  configure(_options: ConfigureOptions): Promise<void> {
    throw new Error('Method not implemented.');
  }
  migrate(): Promise<MigrateResult> {
    throw new Error('Method not implemented.');
  }
  removeOld(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  get = async (options: { key: string }) => {
    const value = localStorage.getItem(options.key);
    return { value };
  };
  set = async (options: { key: string; value: string }) => {
    localStorage.setItem(options.key, options.value);
    return Promise.resolve();
  };

  remove = async (options: { key: string }) => {
    localStorage.removeItem(options.key);
  };
  clear = async () => {
    localStorage.clear();
  };
  keys = async () => {
    return { keys: Object.keys(localStorage) };
  };
}

export class CapacitorCache implements AsyncStorage {
  private store: Record<string, string | null> = {};
  private userId: string | undefined = undefined; // userId for which the cache is initialized
  private driver = isCapacitorNative() ? Preferences : new AsyncLocalStorage();

  constructor(userId?: string) {
    this.userId = userId;
  }

  // Namespace every read/write to a user so one account's persisted redux
  // state can never rehydrate into another on the same device. Pass undefined
  // to fall back to the shared (unscoped) namespace, e.g. logged-out startup.
  setUserId(userId?: string) {
    this.userId = userId;
  }

  getUserId(): string | undefined {
    return this.userId;
  }

  async init(userId?: string) {
    const startedAt = Math.round(performance.now());
    if (userId) {
      this.userId = userId;
    }
    try {
      const { keys } = await this.driver.keys();
      console.info('[CapacitorStorage.init] keys loaded', {
        keyCount: keys.length,
        userScoped: Boolean(userId || this.userId),
        elapsedMs: Math.round(performance.now()) - startedAt,
      });
      // driver.keys() returns full storage keys, which already carry any userId
      // prefix — prefixing again here would read nonexistent "id-id-key" keys.
      await Promise.all(
        keys.map(async (k) => {
          const { value } = await this.driver.get({ key: k });
          this.store[k] = value ?? null;
        }),
      );
      console.info('[CapacitorStorage.init] finished', {
        cachedKeyCount: Object.keys(this.store).length,
        elapsedMs: Math.round(performance.now()) - startedAt,
      });
    } catch (e) {
      reportError(e, 'storage.init');
      throw e;
    }
  }

  getItem = async (k: string) => {
    if (this.userId) {
      k = `${this.userId}-${k}`;
    }
    const cached = this.store[k];
    // Any cached string (including empty) is a hit; null/undefined falls
    // through to the driver so the cache self-repairs from durable storage.
    if (typeof cached === 'string') {
      return cached;
    }
    const { value } = await this.driver.get({ key: k });
    this.store[k] = value;
    return value;
  };
  setItem = (k: string, v: string) => {
    if (this.userId) {
      k = `${this.userId}-${k}`;
    }
    this.store[k] = v;
    return this.driver.set({ key: k, value: v }).catch((e) => reportError(e, 'storage.set'));
  };
  removeItem = (k: string) => {
    if (this.userId) {
      k = `${this.userId}-${k}`;
    }
    delete this.store[k];
    return this.driver.remove({ key: k }).catch((e) => reportError(e, 'storage.remove'));
  };
  clear = () => {
    this.store = {};
    return this.driver.clear().catch((e) => reportError(e, 'storage.clear'));
  };
  key = (i: number) => Promise.resolve(Object.keys(this.store)[i] ?? null);
  get length() {
    return Promise.resolve(Object.keys(this.store).length);
  }
}

export default new CapacitorCache();

export function makeAsyncAdapter(cache: CapacitorCache) {
  return {
    getItem: (k: string) => Promise.resolve(cache.getItem(k)),
    setItem: (k: string, v: string) => {
      cache.setItem(k, v);
      return Promise.resolve();
    },
    removeItem: (k: string) => {
      cache.removeItem(k);
      return Promise.resolve();
    },
  } as const;
}
